import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Flag, ArrowRight, TrendingUp, Timer, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useApp();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const enter = async () => {
    setLoading(true);
    try {
      await login();
      nav("/");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative sidebar-grain hidden lg:flex flex-col justify-between p-14 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1605144884374-ecbb643615f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
            <Flag size={18} className="text-white" />
          </div>
          <span className="font-display text-xl font-semibold text-white tracking-tight">ScratchHQ</span>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-5xl font-light text-white leading-[1.05] tracking-tight">
            Run a better<br />golf course.
          </h1>
          <p className="mt-6 text-white/60 text-lg font-light">
            Revenue. Pace. Experience.<br />One operating system.
          </p>
          <div className="mt-10 space-y-4">
            {[
              [TrendingUp, "See what's happening, not just what happened"],
              [Timer, "Catch pace bottlenecks before they cost you"],
              [Sparkles, "An AI analyst that tells you what to do next"],
            ].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3 text-white/75 text-sm">
                <Icon size={16} className="text-[#7FB89B]" />
                {text}
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-white/35 text-xs">
          Monarch Bay Golf Club · San Leandro, California
        </div>
      </div>

      {/* Right login */}
      <div className="flex items-center justify-center p-8 bg-[#F7F9F8]">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-9 h-9 rounded-lg bg-[#0F3F2D] flex items-center justify-center">
              <Flag size={18} className="text-white" />
            </div>
            <span className="font-display text-xl font-semibold tracking-tight">ScratchHQ</span>
          </div>

          <div className="overline">Demo Environment</div>
          <h2 className="font-display text-3xl font-semibold tracking-tight mt-2 text-[#1C1F1E]">
            Command Center
          </h2>
          <p className="text-[#52525B] mt-2 text-sm">
            Sign in to Monarch Bay Golf Club's operations platform.
          </p>

          <div className="mt-8 p-4 rounded-xl border border-[#E5E7EB] bg-white flex items-center gap-3">
            <img
              src="https://images.pexels.com/photos/8937548/pexels-photo-8937548.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200"
              alt="Jake"
              className="w-11 h-11 rounded-full object-cover"
            />
            <div>
              <div className="font-semibold text-sm text-[#1C1F1E]">Jake Marsden</div>
              <div className="text-xs text-[#52525B]">General Manager</div>
            </div>
          </div>

          <Button
            data-testid="demo-login-btn"
            onClick={enter}
            disabled={loading}
            className="w-full mt-6 h-12 bg-[#0F3F2D] hover:bg-[#165c42] text-white rounded-lg font-medium active:scale-[0.98] transition-transform group"
          >
            {loading ? "Entering..." : "Enter Command Center"}
            <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>

          <p className="text-xs text-[#A1A1AA] mt-6 text-center">
            No password required · Persistent demo environment
          </p>
        </div>
      </div>
    </div>
  );
}
